<template>
  <div id="data-list-list-view" class="data-list-container">
    <!-- Add Article Prompt -->
    <vs-prompt
      title="Add new Article"
      @cancel="val = ''"
      @accept="acceptAddArticlePrompt"
      @close="closeAddArticlePrompt"
      :active.sync="addArticlePrompt"
    >
      <div>
        <div class="vx-row mb-6">
          <div class="vx-col sm:w-5/5 w-full">
            <i8n-input
              label="Title"
              :content.sync="newArticleTitle"
              class="mt-5 w-full"
              name="item-name"
              v-validate="'required'"
            />
          </div>
        </div>
      </div>
    </vs-prompt>

    <!-- Article Category Title -->
    <h3>{{ $translate(articleCategoryTitle) }}</h3>

    <vs-table
      v-if="articles"
      ref="table"
      v-model="selected"
      class="mt-8"
      :data="articles"
      @selected="editData(selected.articleId)"
    >
      <div
        slot="header"
        class="flex flex-wrap-reverse items-center flex-grow justify-between"
      >
        <!-- Add New Button -->
        <div
          class="flex flex-wrap-reverse items-center data-list-btn-container"
        >
          <div
            @click="addArticlePrompt = true"
            class="btn-add-new p-3 mb-4 mr-4 rounded-lg cursor-pointer flex items-center justify-center text-lg font-medium text-base text-primary border border-solid border-primary"
          >
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" />
            <span class="ml-2 text-base text-primary">Add Article</span>
          </div>
        </div>

        <!-- Search Field -->
        <div class="vs-table--search">
          <input
            v-model="query"
            type="text"
            @keyup="handleSearch"
            class="vs-table--search-input search-field p-2 rounded border border-gray-300 focus:outline-none focus:ring focus:ring-primary"
            placeholder="Search..."
          />
          <i class="vs-icon-search absolute top-2 right-2 text-gray-400"></i>
        </div>
      </div>

      <template slot="thead">
        <vs-th sort-key="title">Title</vs-th>
        <vs-th sort-key="startDate">Start Date</vs-th>
        <vs-th sort-key="endDate">End Date</vs-th>
        <vs-th sort-key="isActive">Status</vs-th>
        <vs-th>Action</vs-th>
      </template>

      <template slot-scope="{ data }">
        <tbody>
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              <p class="product-name font-medium truncate">
                {{ $translate(tr.title) }}
              </p>
            </vs-td>
            <vs-td>
              <p class="product-category">{{ tr.startDate | formatDate }}</p>
            </vs-td>
            <vs-td>
              <p class="product-category" v-if="tr.hasEndAvailability">
                {{ tr.endDate | formatDate }}
              </p>
            </vs-td>
            <vs-td>
              <vs-chip
                :color="getStatusColor(tr.isActive)"
                class="product-order-status"
              >
                {{ tr.isActive ? "Active" : "Disabled" }}
              </vs-chip>
            </vs-td>

            <vs-td class="whitespace-no-wrap">
              <feather-icon
                icon="TrashIcon"
                svgClasses="w-5 h-5 hover:text-danger stroke-current"
                class="ml-2"
                @click.stop="confirmDelete(tr.articleId)"
              />
            </vs-td>
          </vs-tr>
        </tbody>
      </template>
    </vs-table>

    <!-- Pagination -->
    <vs-pagination
      :total="Math.ceil(totalItems / itemsPerPage)"
      :page.sync="currentPage"
      @change="handlePageChange"
    />
  </div>
</template>

<script>
export default {
  props: {
    articleCategoryInstance: Object,
  },
  data() {
    return {
      addArticlePrompt: false,
      newArticleTitle: null,

      articleCategoryTitle: "",
      query: "", // This is what the user sees in the input

      articles: [],
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: 50,

      selected: [],
      itemIdToDelete: null,
    };
  },
  methods: {
    // Fetch articles from the API with pagination and search query
    getArticles: function () {
      this.$vs.loading();

      const url = `${process.env.VUE_APP_API}/ArticleCategories/${this.articleCategoryInstance.articleCategoryId}`;
      const params = {
        query: this.query.trim() === "" ? "*" : this.query.trim(), // Send '*' if input is empty
        page: this.currentPage,
        pageSize: this.itemsPerPage,
      };

      this.$http
        .get(url, { params })
        .then((resp) => {
          this.articles = resp.data.items;
          this.totalItems = resp.data.totalItems;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },
    // Triggered on pagination change
    handlePageChange(page) {
      this.currentPage = page;
      this.getArticles();
    },
    // Triggered when the user presses Enter in the search input
    handleSearch(event) {
      if (event.key === "Enter") {
        this.currentPage = 1; // Reset to first page
        this.getArticles(); // Fetch articles with current query
      }
    },
    confirmDelete(itemId) {
      this.itemIdToDelete = itemId;

      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `Confirm`,
        text: "Are you sure you want to delete the Article?",
        accept: this.deleteItem,
      });
    },
    editData(articleId) {
      this.$router
        .push({
          name: "admin-content-article",
          params: {
            articleCategoryId: this.articleCategoryInstance.articleCategoryId,
            articleId: articleId,
          },
        })
        .catch(() => {});
    },
    deleteItem() {
      const url = `${process.env.VUE_APP_API}/Articles/${this.itemIdToDelete}`;

      this.$http
        .delete(url)
        .then(() => {
          this.$vs.notify({
            color: "success",
            title: "Success",
            text: "Article deleted",
          });

          this.getArticles();
        })
        .catch((err) => {
          console.error(err);

          this.$vs.notify({
            color: "danger",
            title: "Error",
            text: "Could not delete Article: " + err,
          });
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },
    getStatusColor(isActive) {
      return isActive ? "success" : "warning";
    },
    acceptAddArticlePrompt() {
      this.$vs.loading();

      const url = `${process.env.VUE_APP_API}/Articles/${this.articleCategoryInstance.articleCategoryId}`;
      const payload = { title: this.newArticleTitle };

      this.$http
        .post(url, payload)
        .then((resp) => {
          this.$vs.notify({
            color: "success",
            title: "Success",
            text: "Article added",
          });

          this.$router
            .push({
              name: "admin-content-article",
              params: {
                articleCategoryId:
                  this.articleCategoryInstance.articleCategoryId,
                articleId: resp.data.articleId,
              },
            })
            .catch(() => {});
        })
        .catch((err) => {
          console.error(err);

          this.$vs.notify({
            color: "danger",
            title: "Error",
            text: "Could not add Article: " + err,
          });
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },
  },
  created() {
    this.getArticles();
  },
  mounted() {
    this.articleCategoryTitle = this.articleCategoryInstance.title;
  },
};
</script>
<style lang="scss">
.search-field {
  color: rgba(0, 0, 0, 0.7);
  border-radius: 20px !important;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 12px 5px 12px 10px !important;
}
.status-category {
  display: flex;
  align-items: center;
}

.status-circle {
  width: 14px;
  height: 14px;
  min-width: 14px;
  min-height: 14px;
  margin-right: 5px;
}

#data-list-list-view {
  .vs-con-table {
    /*
      Below media-queries is fix for responsiveness of action buttons
      Note: If you change action buttons or layout of this page, Please remove below style
    */
    @media (max-width: 689px) {
      .vs-table--search {
        margin-left: 0;
        max-width: unset;
        width: 100%;

        .vs-table--search-input {
          width: 100%;
        }
      }
    }

    @media (max-width: 461px) {
      .items-per-page-handler {
        display: none;
      }
    }

    @media (max-width: 341px) {
      .data-list-btn-container {
        width: 100%;

        .dd-actions,
        .btn-add-new {
          width: 100%;
          margin-right: 0 !important;
        }
      }
    }

    .product-name {
      max-width: 23rem;
    }

    .vs-table--header {
      display: flex;
      flex-wrap: wrap;
      margin-left: 1.5rem;
      margin-right: 1.5rem;
      > span {
        display: flex;
        flex-grow: 1;
      }

      .vs-table--search {
        padding-top: 0;

        .vs-table--search-input {
          padding: 0.9rem 2.5rem;
          font-size: 1rem;

          & + i {
            left: 1rem;
          }

          &:focus + i {
            left: 1rem;
          }
        }
      }
    }

    .vs-table {
      border-collapse: separate;
      border-spacing: 0 1.3rem;
      padding: 0 1rem;

      tr {
        box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
        td {
          padding: 20px;
          &:first-child {
            border-top-left-radius: 0.5rem;
            border-bottom-left-radius: 0.5rem;
          }
          &:last-child {
            border-top-right-radius: 0.5rem;
            border-bottom-right-radius: 0.5rem;
          }
        }
        td.td-check {
          padding: 20px !important;
        }
      }
    }

    .vs-table--thead {
      th {
        padding-top: 0;
        padding-bottom: 0;

        .vs-table-text {
          text-transform: uppercase;
          font-weight: 600;
        }
      }
      th.td-check {
        padding: 0 15px !important;
      }
      tr {
        background: none;
        box-shadow: none;
      }
    }

    .vs-table--pagination {
      justify-content: center;
    }
  }
}
</style>